import React from 'react';

import { statusSpec } from '../WeekCalendar';
import type { DayStatus } from '../WeekCalendar';
import {
  NoteRoot,
  StatusPill,
  DottedDivider,
  TextBlock,
  NoteTitle,
  NoteDescription,
} from './styles';

/** Status que podem ser selecionados (dias indisponíveis não geram nota). */
export type RouteNoteStatus = Exclude<DayStatus, 'unavailable'>;

/**
 * Conteúdo e cor padrão por status.
 * Texto de "off-route" é o do Figma (node 10187-55151); o de "route"
 * deriva do tooltip do calendário legado ("equipe estará na sua região").
 */
const DEFAULT_CONTENT: Record<
  RouteNoteStatus,
  {
    title: string;
    description: string;
    pillBg: string;
    pillBorder: string;
    pillText: string;
  }
> = {
  route: {
    title: 'Dia de rota: sua entrega com mais agilidade',
    description:
      'Ao escolher um "Dia de rota", nossa equipe já estará na sua região — o que torna o abastecimento mais rápido e a programação mais precisa.',
    pillBg: '#E5E7FF',
    pillBorder: '#000FFF',
    pillText: '#003333',
  },
  'off-route': {
    title: 'Neste dia, não temos previsão de entrega em sua região',
    description:
      'Entraremos em contato em breve com a confirmação ou uma nova sugestão de data',
    pillBg: '#F2F2F2',
    pillBorder: '#595959',
    pillText: '#003333',
  },
};

export interface RouteStatusNoteProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Status do dia selecionado. */
  status: RouteNoteStatus;
  /** Sobrescreve o texto do pill (default: label do status). */
  label?: React.ReactNode;
  /** Sobrescreve o título (negrito). */
  title?: React.ReactNode;
  /** Sobrescreve a descrição (regular). */
  description?: React.ReactNode;
}

/**
 * RouteStatusNote — nota explicativa do status de rota do dia escolhido.
 *
 * Exibe um pill com o status ("Dia de rota" / "Fora de rota"), um divisor
 * pontilhado e um texto explicando o que aquele status significa para a
 * entrega. Aparece abaixo do calendário, reagindo ao dia selecionado.
 *
 * @example
 * <RouteStatusNote status="off-route" />
 */
export const RouteStatusNote = ({
  status,
  label,
  title,
  description,
  ...rest
}: RouteStatusNoteProps): React.ReactElement => {
  const content = DEFAULT_CONTENT[status];
  const resolvedDescription = description ?? content.description;

  return (
    <NoteRoot role="note" {...rest}>
      <StatusPill
        $bg={content.pillBg}
        $border={content.pillBorder}
        $text={content.pillText}
      >
        {label ?? statusSpec[status].label}
      </StatusPill>
      <DottedDivider aria-hidden="true" />
      <TextBlock>
        <NoteTitle>{title ?? content.title}</NoteTitle>
        {resolvedDescription && (
          <NoteDescription>{resolvedDescription}</NoteDescription>
        )}
      </TextBlock>
    </NoteRoot>
  );
};

export default RouteStatusNote;
